/**
 * Dashboard Designer's own types/client — copied from IMS, with CRM
 * datasources instead of Incident and Actions.
 */
import { api } from "../../../lib/api";
import type { DashboardChartColorScheme } from "./chartColorSchemes";

export type { DashboardChartColorScheme } from "./chartColorSchemes";

export type DashboardSectionType = "kpi" | "charts" | "table" | "text";
export type DashboardChartType =
  | "bar" | "bar_horizontal" | "bar_clustered" | "bar_stacked" | "bar_double"
  | "line" | "area"
  | "pie" | "doughnut"
  | "scatter" | "bubble"
  | "funnel" | "waterfall"
  | "heatmap_bar" | "heatmap_grid" | "calendar"
  | "word_cloud" | "tornado"
  | "sparkline" | "gauge"
  | "number_trend";
export type DashboardAggregation = "count" | "sum" | "avg" | "min" | "max" | "count_distinct";

export interface DashboardKpiItem {
  id: string;
  label: string;
  aggregation: DashboardAggregation;
  column: string | null;
  dateField: string | null;
  filters: unknown[];
  width: number;
  dataset?: string | null;
}

export type DashboardChartOrientation = "vertical" | "horizontal";
export type DashboardChartValueFormat = "actual" | "percent" | "money";

export interface DashboardChartItem {
  id: string;
  title: string;
  chartType: DashboardChartType;
  groupBy: string;
  aggregation: DashboardAggregation;
  column: string | null;
  titleColor?: string;
  orientation?: DashboardChartOrientation;
  valueField?: string | null;
  dateField?: string | null;
  fieldDisplayName?: string;
  axisUnit?: string;
  colorScheme?: DashboardChartColorScheme;
  showLegend?: boolean;
  showValues?: boolean;
  valueFormat?: DashboardChartValueFormat;
  tooltip?: string;
  colStart?: number;
  colSpan?: number;
  stackBy?: string | null;
  queryMode?: "closed_vs_target" | "pipeline_total" | null;
}

export interface DashboardTableItem {
  id: string;
  title: string;
  columns: string[];
}

export interface DashboardTextItem {
  id: string;
  text: string;
}

export interface DashboardSection {
  id: string;
  type: DashboardSectionType;
  title: string;
  items: (DashboardKpiItem | DashboardChartItem | DashboardTableItem | DashboardTextItem)[];
}

export interface DashboardLayout {
  version: 1;
  title: string;
  eyebrow?: string;
  sections: DashboardSection[];
}

export interface DashboardTemplate {
  id: string;
  source_app_code: string;
  name: string;
  layout_json: DashboardLayout;
  soft_deleted: boolean;
  created_at: string;
  updated_at: string;
  kind?: string | null;
  is_default?: boolean;
}

export type DashboardFieldGroup = string;

export interface DashboardFieldInfo {
  key: string;
  label: string;
  kind: "groupable" | "date" | "numeric";
  group: DashboardFieldGroup;
  entity?: string;
}

export interface DashboardSourceInfo {
  app_code: string;
  label: string;
  date_fields: string[];
  groupable_fields: string[];
  numeric_fields: string[];
  field_labels: Record<string, string>;
  fields: DashboardFieldInfo[];
  datasets?: { key: string; label: string }[];
}

export interface DashboardSourceResponse {
  sources: Record<string, DashboardSourceInfo>;
}

export interface DashboardShare {
  user_id: string;
  display_name: string;
  created_at: string;
}

type KpiBody = {
  aggregation: string;
  column?: string | null;
  date_field?: string | null;
  start?: string | null;
  end?: string | null;
  dataset?: string | null;
};
type ChartBody = {
  group_by: string;
  aggregation: string;
  column?: string | null;
  date_field?: string | null;
  start?: string | null;
  end?: string | null;
  stack_by?: string | null;
  query_mode?: string | null;
};
type TableBody = { columns: string[] };

const scopedBase = (org: string) => `/org/${org}/crm/dashboards`;
const mineBase = (org: string) => `/org/${org}/crm/dashboards/mine`;

export const dashboardDesignerApi = {
  list: (org: string, signal?: AbortSignal) => api.get<DashboardTemplate[]>(`${scopedBase(org)}/templates`, signal),
  get: (org: string, id: string, signal?: AbortSignal) => api.get<DashboardTemplate>(`${scopedBase(org)}/templates/${id}`, signal),
  create: (org: string, name: string, template?: "sales") => api.post<DashboardTemplate>(`${scopedBase(org)}/templates`, { name, template }),
  update: (org: string, id: string, patch: { name?: string; layout_json?: DashboardLayout }) =>
    api.put<DashboardTemplate>(`${scopedBase(org)}/templates/${id}`, patch),
  remove: (org: string, id: string) => api.delete(`${scopedBase(org)}/templates/${id}`),
  fieldCatalog: (org: string, signal?: AbortSignal) => api.get<DashboardSourceResponse>(`${scopedBase(org)}/field-catalog`, signal),
  kpiData: (org: string, id: string, body: KpiBody) => api.post<{ value: number | null }>(`${scopedBase(org)}/templates/${id}/kpi-data`, body),
  chartData: (org: string, id: string, body: ChartBody) =>
    api.post<{
      rows: { label: string; value: number }[];
      categories?: string[];
      series?: { label: string; data: number[] }[];
      meta?: { actual?: number; target?: number; period_label?: string };
    }>(`${scopedBase(org)}/templates/${id}/chart-data`, body),
  tableData: (org: string, id: string, body: TableBody) => api.post<{ rows: Record<string, unknown>[] }>(`${scopedBase(org)}/templates/${id}/table-data`, body),
  members: (org: string) => api.get<{ id: string; display_name: string }[]>(`${scopedBase(org)}/members`),
  listShares: (org: string, id: string) => api.get<DashboardShare[]>(`${scopedBase(org)}/templates/${id}/shares`),
  addShare: (org: string, id: string, userId: string) => api.post<DashboardShare>(`${scopedBase(org)}/templates/${id}/shares`, { user_id: userId }),
  removeShare: (org: string, id: string, userId: string) => api.delete(`${scopedBase(org)}/templates/${id}/shares/${userId}`),
};

export const dashboardMineApi = {
  list: (org: string, signal?: AbortSignal) => api.get<DashboardTemplate[]>(`${mineBase(org)}`, signal),
  get: (org: string, id: string, signal?: AbortSignal) => api.get<DashboardTemplate>(`${mineBase(org)}/${id}`, signal),
  kpiData: (org: string, id: string, body: KpiBody) => api.post<{ value: number | null }>(`${mineBase(org)}/${id}/kpi-data`, body),
  chartData: (org: string, id: string, body: ChartBody) =>
    api.post<{
      rows: { label: string; value: number }[];
      categories?: string[];
      series?: { label: string; data: number[] }[];
      meta?: { actual?: number; target?: number; period_label?: string };
    }>(`${mineBase(org)}/${id}/chart-data`, body),
  tableData: (org: string, id: string, body: TableBody) => api.post<{ rows: Record<string, unknown>[] }>(`${mineBase(org)}/${id}/table-data`, body),
};

let counter = 0;
export function newSectionId(): string {
  counter += 1;
  return `section_${Date.now()}_${counter}`;
}
export function newItemId(prefix: string): string {
  counter += 1;
  return `${prefix}_${Date.now()}_${counter}`;
}

export function blankLayout(title: string): DashboardLayout {
  return {
    version: 1,
    title,
    eyebrow: "Dashboard",
    sections: [
      { id: newSectionId(), type: "kpi", title: "Overview", items: [
        { id: newItemId("kpi"), label: "Total leads", aggregation: "count", column: null, dateField: null, filters: [], width: 1, dataset: "lead" },
      ] },
      { id: newSectionId(), type: "charts", title: "By status", items: [
        { id: newItemId("chart"), title: "Leads by status", chartType: "bar", groupBy: "lead.lead_status", aggregation: "count", column: null },
      ] },
    ],
  };
}

export function normalizeDashboardLayout(layout: DashboardLayout | undefined | null, name: string): DashboardLayout {
  if (layout && Array.isArray(layout.sections)) {
    return { ...layout, title: layout.title || name, sections: layout.sections };
  }
  return blankLayout(name);
}

export const SECTION_TYPE_LABELS: Record<DashboardSectionType, string> = {
  kpi: "KPI row",
  charts: "Chart row",
  table: "Table",
  text: "Text block",
};

export const CHART_TYPE_LABELS: Record<DashboardChartType, string> = {
  bar: "Bar",
  bar_horizontal: "Bar (horizontal)",
  bar_clustered: "Bar (clustered)",
  bar_stacked: "Bar (stacked)",
  bar_double: "Double bar",
  line: "Line",
  area: "Area",
  pie: "Pie",
  doughnut: "Doughnut",
  scatter: "Scatter",
  bubble: "Bubble",
  funnel: "Funnel",
  waterfall: "Waterfall",
  heatmap_bar: "Heatmap (bar-based)",
  heatmap_grid: "Heatmap (grid)",
  calendar: "Calendar",
  word_cloud: "Word cloud",
  tornado: "Tornado",
  sparkline: "Micro chart / sparkline",
  gauge: "Gauge / speedometer",
  number_trend: "Number + trend",
};

export const AGGREGATION_LABELS: Record<DashboardAggregation, string> = {
  count: "Count",
  sum: "Sum",
  avg: "Average",
  min: "Minimum",
  max: "Maximum",
  count_distinct: "Count distinct",
};

export const FIELD_GROUP_LABELS: Record<string, string> = {
  company: "Companies",
  contact: "Contacts",
  lead: "Leads",
  product: "Products",
  opportunity: "Opportunities",
  quote: "Quotes",
  order: "Orders",
  invoice: "Invoices",
  sales_cycle: "Sales cycle",
  activity: "Activities",
  support: "Support",
  calendar: "Calendar",
  campaign: "Campaigns",
  forecast: "Forecast",
  flex: "Custom / flex fields",
};

export const FIELD_GROUP_ORDER = [
  "company", "contact", "lead", "product", "opportunity", "quote", "order",
  "invoice", "sales_cycle", "activity", "support", "calendar", "campaign", "forecast", "flex",
];
